'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { LETTER_SECTIONS } from '@/app/open-letter/data/letterData';

interface SectionMeta {
  id: string;
  label: string;
  number: number;
}

const SECTIONS: SectionMeta[] = LETTER_SECTIONS.map(s => ({
  id: s.id,
  label: s.sectionTitle,
  number: s.sectionNumber,
}));

export default function LetterProgressBar() {
  const [progress, setProgress] = useState(0);
  const [activeSection, setActiveSection] = useState<string>('');
  const [tocOpen, setTocOpen] = useState(false);

  const updateProgress = useCallback(() => {
    const scrollTop = window.scrollY;
    const docHeight = document.documentElement.scrollHeight - window.innerHeight;
    const pct = docHeight > 0 ? Math.min(100, (scrollTop / docHeight) * 100) : 0;
    setProgress(pct);

    // Determine active section
    for (let i = SECTIONS.length - 1; i >= 0; i--) {
      const el = document.getElementById(`section-${SECTIONS[i].id}`);
      if (el) {
        const rect = el.getBoundingClientRect();
        if (rect.top <= 120) {
          setActiveSection(SECTIONS[i].id);
          break;
        }
      }
    }
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', updateProgress, { passive: true });
    updateProgress();
    return () => window.removeEventListener('scroll', updateProgress);
  }, [updateProgress]);

  const scrollToSection = (id: string) => {
    const el = document.getElementById(`section-${id}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      // Offset for sticky header
      setTimeout(() => {
        window.scrollBy({ top: -80, behavior: 'smooth' });
      }, 300);
    }
    setTocOpen(false);
  };

  return (
    <>
      {/* Progress bar — fixed at top below header */}
      <div style={{
        position: 'fixed',
        top: 64,
        left: 0,
        right: 0,
        height: 3,
        background: 'rgba(201,168,76,0.1)',
        zIndex: 49,
      }}>
        <div style={{
          height: '100%',
          width: `${progress}%`,
          background: 'linear-gradient(90deg, rgba(45,122,71,0.8), #c9a84c)',
          transition: 'width 0.1s linear',
          boxShadow: '0 0 8px rgba(201,168,76,0.4)',
        }} />
      </div>

      {/* TOC toggle button — fixed right side */}
      <div style={{
        position: 'fixed',
        right: 16,
        top: '50%',
        transform: 'translateY(-50%)',
        zIndex: 48,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: 4,
      }}>
        {/* Section dots */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginBottom: 8 }}>
          {SECTIONS.map(s => (
            <button
              key={s.id}
              onClick={() => scrollToSection(s.id)}
              title={s.label}
              style={{
                width: activeSection === s.id ? 10 : 6,
                height: activeSection === s.id ? 10 : 6,
                borderRadius: '50%',
                background: activeSection === s.id ? '#c9a84c' : 'rgba(201,168,76,0.25)',
                border: 'none',
                cursor: 'pointer',
                padding: 0,
                transition: 'all 0.2s',
                boxShadow: activeSection === s.id ? '0 0 6px rgba(201,168,76,0.6)' : 'none',
                alignSelf: 'flex-end',
              }}
              aria-label={`Jump to: ${s.label}`}
            />
          ))}
        </div>

        {/* TOC toggle */}
        <button
          onClick={() => setTocOpen(v => !v)}
          style={{
            background: tocOpen ? 'rgba(201,168,76,0.15)' : 'rgba(6,13,18,0.85)',
            border: '1px solid rgba(201,168,76,0.3)',
            color: '#c9a84c',
            cursor: 'pointer',
            padding: '6px 10px',
            fontSize: 9,
            letterSpacing: '.1em',
            fontFamily: 'monospace',
            textTransform: 'uppercase',
            borderRadius: 3,
            backdropFilter: 'blur(8px)',
            transition: 'all 0.2s',
          }}
          aria-label="Toggle table of contents"
        >
          {tocOpen ? '✕ Close' : '≡ Sections'}
        </button>

        {/* Progress label */}
        <div style={{
          fontSize: 9,
          color: 'rgba(201,168,76,0.5)',
          fontFamily: 'monospace',
          letterSpacing: '.05em',
          textAlign: 'right',
        }}>
          {Math.round(progress)}%
        </div>
      </div>

      {/* TOC Drawer */}
      {tocOpen && (
        <>
          <div
            onClick={() => setTocOpen(false)}
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 46,
            }}
          />
          <div style={{
            position: 'fixed',
            right: 56,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 47,
            background: 'rgba(6,13,18,0.97)',
            border: '1px solid rgba(201,168,76,0.2)',
            borderRadius: 6,
            padding: '12px 0',
            minWidth: 220,
            maxHeight: '70vh',
            overflowY: 'auto',
            boxShadow: '0 16px 48px rgba(0,0,0,0.7)',
            backdropFilter: 'blur(12px)',
            animation: 'fadeInScale 0.2s ease',
          }}>
            <style>{`
              @keyframes fadeInScale {
                from { opacity: 0; transform: translateY(-50%) scale(0.95); }
                to { opacity: 1; transform: translateY(-50%) scale(1); }
              }
            `}</style>
            <div style={{
              fontSize: 8,
              color: '#c9a84c',
              letterSpacing: '.15em',
              textTransform: 'uppercase',
              padding: '0 16px 10px',
              borderBottom: '1px solid rgba(201,168,76,0.1)',
              marginBottom: 4,
            }}>
              ✦ Open Letter — Sections
            </div>
            {SECTIONS.map(s => (
              <button
                key={s.id}
                onClick={() => scrollToSection(s.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  width: '100%',
                  padding: '8px 16px',
                  background: activeSection === s.id ? 'rgba(201,168,76,0.08)' : 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'background 0.15s',
                }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(201,168,76,0.06)'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = activeSection === s.id ? 'rgba(201,168,76,0.08)' : 'transparent'; }}
              >
                <span style={{
                  fontSize: 9,
                  color: activeSection === s.id ? '#c9a84c' : 'rgba(201,168,76,0.3)',
                  fontFamily: 'monospace',
                  minWidth: 20,
                  flexShrink: 0,
                }}>
                  {s.number.toString().padStart(2, '0')}
                </span>
                <span style={{
                  fontSize: 11,
                  color: activeSection === s.id ? '#d4c5a9' : '#7a6f5d',
                  lineHeight: 1.3,
                  fontFamily: "'Georgia', serif",
                }}>
                  {s.label}
                </span>
                {activeSection === s.id && (
                  <span style={{ marginLeft: 'auto', fontSize: 8, color: '#c9a84c' }}>◀</span>
                )}
              </button>
            ))}
          </div>
        </>
      )}
    </>
  );
}
